import { IArticle } from '@/types'
import classNames from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { PropsWithChildren } from 'react'
import { getImageUrl } from 'utils/image'
import { getArticleUrl } from 'utils/urls'

interface ArticleCardProps {
  article: IArticle
  className?: string
  imageAspectRatio?: 'responsive' | '4/3'
  imageFormat?: 'auto' | 'fullsize'
}

function getImages(
  article: IArticle,
  imageFormat: ArticleCardProps['imageFormat'] = 'auto'
) {
  const image = article.attributes?.articleImage?.data
  const thumbnail = article.attributes?.thumbnailImage?.data

  if (imageFormat === 'fullsize') {
    return {
      mobile: image?.attributes.url || getImageUrl(image, 'medium'),
      desktop: thumbnail?.attributes.url || getImageUrl(thumbnail, 'large'),
    }
  }

  return {
    mobile: getImageUrl(image, 'medium'),
    desktop: getImageUrl(thumbnail, 'large'),
  }
}

export const ArticleCard = ({
  article,
  className,
  imageAspectRatio = 'responsive',
  imageFormat,
}: ArticleCardProps) => {
  const { mobile, desktop } = getImages(article, imageFormat)

  const hasCategory = Boolean(article?.attributes?.category?.data)
  const hasTags = Boolean(article?.attributes?.tags?.data?.length)
  const displayTags = hasCategory || hasTags

  return (
    <Link
      href={getArticleUrl(
        article.attributes?.slug,
        article.attributes?.category?.data?.attributes?.title || null,
        article.id
      )}
      className={classNames(className, 'group')}
    >
      <div className='flex flex-col items-start gap-3'>
        <div
          className='relative h-0 w-full xl:hidden block overflow-hidden'
          style={{ paddingBottom: imageAspectRatio === '4/3' ? '75%' : '100%' }}
        >
          <Image
            src={mobile}
            layout='fill'
            alt={
              article.attributes?.thumbnailImage.data?.attributes
                .alternativeText || 'Article cover image'
            }
            className='rounded object-cover object-center group-hover:scale-[1.08] duration-300 ease-in-out'
            quality={50}
          />
        </div>
        <div
          className='relative h-0 w-full hidden xl:block overflow-hidden'
          style={{ paddingBottom: imageAspectRatio === '4/3' ? '75%' : '100%' }}
        >
          <Image
            src={desktop}
            layout='fill'
            alt={
              article.attributes?.thumbnailImage.data?.attributes
                .alternativeText || 'Article cover image'
            }
            className='rounded object-cover object-center group-hover:scale-[1.08] duration-300 ease-in-out'
            quality={50}
          />
        </div>

        {displayTags && (
          <div className='flex flex-wrap items-center space-x-2'>
            {hasTags &&
              article?.attributes?.tags?.data?.map(tag => (
                <Tag key={tag.id}>{tag.attributes.name}</Tag>
              ))}

            {!hasTags && hasCategory && (
              <Tag>{article?.attributes?.category?.data?.attributes?.name}</Tag>
            )}
          </div>
        )}

        <div className='flex flex-col gap-0.5'>
          <h3 className='font-body text-base tracking-normal text-cool-900'>
            {article.attributes?.title}
          </h3>

          <p className='font-body text-sm text-cool-600'>
            {article.attributes?.summary}
          </p>
        </div>
      </div>
    </Link>
  )
}

function Tag({ children }: PropsWithChildren) {
  return (
    <div className='flex rounded-full border border-cool-800 px-2.5 py-1 font-body text-xs'>
      {children}
    </div>
  )
}
